import React, { useRef, useEffect, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useSimulation } from '../state'
import { getPluginAsync } from '../plugins/registry'

export const PluginPanel = ({ inputState, onAllComplete, resumeState }) => {
  const containerRef = useRef(null)
  const pluginRef = useRef(null)
  const outputStateRef = useRef(null)
  const [state, dispatch] = useSimulation()
  const { pluginName, pluginConfig, tasks, allComplete } = state
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const emitTaskComplete = useCallback(
    taskId => {
      dispatch({ type: 'COMPLETE_TASK', payload: taskId })
    },
    [dispatch]
  )

  const emitTaskProgress = useCallback(
    (taskId, data) => {
      dispatch({ type: 'REPORT_PROGRESS', payload: { taskId, data } })
    },
    [dispatch]
  )

  const emitSimulationComplete = useCallback(
    outputState => {
      outputStateRef.current = outputState
      dispatch({ type: 'SET_PLUGIN_OUTPUT', payload: outputState })
    },
    [dispatch]
  )

  const emitResetTasks = useCallback(
    () => {
      dispatch({ type: 'RESET' })
    },
    [dispatch]
  )

  // When all tasks finish, notify the chain (if in chain mode)
  useEffect(() => {
    if (allComplete && onAllComplete) {
      onAllComplete(outputStateRef.current)
    }
  }, [allComplete, onAllComplete])

  useEffect(() => {
    if (!pluginName || !containerRef.current) return

    let mounted = true
    setLoading(true)
    setError(null)

    getPluginAsync(pluginName)
      .then(pluginFactory => {
        if (!mounted) return

        const plugin = typeof pluginFactory === 'function' ? pluginFactory() : pluginFactory
        pluginRef.current = plugin

        const context = {
          tasks: tasks,
          config: pluginConfig,
          emitTaskComplete: emitTaskComplete,
          emitTaskProgress: emitTaskProgress,
          inputState: inputState || null,
          emitSimulationComplete: emitSimulationComplete,
          emitResetTasks: emitResetTasks,
          resumeState: resumeState || null,
        }

        plugin.mount(containerRef.current, context)
        setLoading(false)
      })
      .catch(err => {
        if (!mounted) return
        console.error('Failed to load plugin "' + pluginName + '":', err)
        setError('Failed to load plugin: ' + pluginName)
        setLoading(false)
      })

    return () => {
      mounted = false
      if (pluginRef.current && pluginRef.current.destroy) {
        pluginRef.current.destroy()
        pluginRef.current = null
      }
    }
  }, [pluginName]) // Only re-mount when plugin changes, not on every task update

  return (
    <div className="simulation-plugin-panel">
      {loading && (
        <div style={{ padding: '24px', textAlign: 'center', color: '#666' }}>
          Loading simulation...
        </div>
      )}
      {error && (
        <div style={{ padding: '24px', textAlign: 'center', color: '#dc3545' }}>
          {error}
        </div>
      )}
      <div ref={containerRef} className="simulation-plugin-container" />
    </div>
  )
}

PluginPanel.defaultProps = {
  inputState: null,
  onAllComplete: null,
  resumeState: null,
}

PluginPanel.propTypes = {
  inputState: PropTypes.object,
  onAllComplete: PropTypes.func,
  resumeState: PropTypes.object,
}
